/**
 * Canonical registry of export adapter types — the SINGLE SOURCE OF TRUTH for
 * (a) which adapter types exist and (b) which are streaming-only (i.e. reject
 * an `accumulate` trigger policy because they deliver one document at a time).
 *
 * Consumers derive from this instead of maintaining parallel lists:
 *   - `ExportDestinationContent['adapter']` union (content.ts)
 *   - the web adapter dropdown (`ADAPTER_OPTIONS`)
 *   - the API route's valid-adapter gate (`validAdapters`)
 *   - the API route's streaming-only accumulate guard
 *
 * Adding an adapter = one entry here (plus its class in the API
 * `adapter-registry` switch). Previously these lists drifted — the route's
 * `validAdapters` was missing `email`, so email destinations 400'd on create.
 *
 * Order is the dropdown display order.
 */
export const EXPORT_ADAPTERS = {
    skarn_product: { streamingOnly: true, formatless: false },
    webhook: { streamingOnly: false, formatless: false },
    s3: { streamingOnly: false, formatless: false },
    sftp: { streamingOnly: false, formatless: false },
    filesystem: { streamingOnly: false, formatless: false },
    email: { streamingOnly: false, formatless: false },
    pdf_summary: { streamingOnly: false, formatless: false },
    // F-037 — dossier + document_repository build their own envelope/PDF and bind
    // no ExportFormat. Field mapping for these must run in free-form mode (the
    // operator maps Capture fields → the remote doc-type's keys, with no columns).
    dossier: { streamingOnly: true, formatless: true },
    document_repository: { streamingOnly: true, formatless: true },
    sql: { streamingOnly: false, formatless: false },
};
/** Ordered list of every implemented export adapter type (dropdown order). */
export const EXPORT_ADAPTER_TYPES = Object.keys(EXPORT_ADAPTERS);
/**
 * True when the adapter delivers one document at a time and therefore rejects
 * an `accumulate` trigger policy. Unknown types return false (the route's
 * separate valid-adapter gate rejects those).
 */
export function isStreamingOnlyAdapter(type) {
    return EXPORT_ADAPTERS[type]?.streamingOnly === true;
}
/**
 * True when the adapter binds NO ExportFormat (it renders its own artifact, e.g.
 * dossier's envelope/PDF). The mapping UI runs in free-form mode for these so an
 * operator can add arbitrary target/source rows (mapping Capture fields to the
 * remote doc-type's keys). Unknown types return false. (F-037)
 */
export function isFormatlessAdapter(type) {
    return EXPORT_ADAPTERS[type]?.formatless === true;
}
//# sourceMappingURL=export-adapters.js.map